import {UseDocumentPreviewReturnType} from 'platform/document-preview';
import {match} from 'ts-pattern';

import {useEffect, useEffectEvent, useMemo} from 'react';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {getSafely} from 'shared/web';

import {
  DeliveryNoteDocumentPreviewFormDefinition,
  DeliveryNoteItem,
} from '../types/DeliveryNoteDocumentPreviewFormDefinition';
import {hasItemPriceMismatch} from '../utils/hasItemPriceMismatch';

interface UseInvalidItemsCountParams {
  allowZeroPrices?: boolean;
  isPriceRecalculationEnabled: boolean;
  isConfirmed: boolean;
  pageNumber: number;
  listOfItems: DeliveryNoteDocumentPreviewFormDefinition['listOfItems'];
  documentPreview: UseDocumentPreviewReturnType;
}

interface InvalidItemsCountResult {
  invalidItemsCount: number;
  invalidItemsCountPerPage: number;
  priceMismatchesCount: number;
  priceMismatchesCountPerPage: number;
}

export function useInvalidItemsCount(props: UseInvalidItemsCountParams): InvalidItemsCountResult {
  const result = useMemo(() => {
    if (!props.isConfirmed) {
      return {
        invalidItemsCount: 0,
        invalidItemsCountPerPage: 0,
        invalidCountByPage: {} as Record<number, number>,
        priceMismatchesCount: 0,
        priceMismatchesCountPerPage: 0,
        priceMismatchCountByPage: {} as Record<number, number>,
      };
    }

    const {invalidItemsCount, invalidCountByPage, priceMismatchesCount, priceMismatchCountByPage} =
      (props.listOfItems || []).reduce(
        (acc, item) => {
          if (!item || !item.isActive) {
            return acc;
          }

          const numericFields: (keyof DeliveryNoteItem)[] = ['quantity', 'unitPrice', 'totalPrice'];

          const invalidFieldsCount = Object.keys(item).reduce((count, key) => {
            if (
              key === 'isActive' ||
              key === 'isSelected' ||
              key === 'pageNumber' ||
              key === 'sourceLineNumber' ||
              key === 'lastAutoFilledCatalogNumber'
            ) {
              return count;
            }

            const typedKey = key as keyof DeliveryNoteItem;

            const value = getSafely(item, typedKey);

            if (numericFields.includes(typedKey)) {
              const isZeroAllowed = props.allowZeroPrices && typedKey !== 'quantity';
              if (!isNotNil(value) || (isZeroAllowed ? value < 0 : value <= 0)) {
                return count + 1;
              }
              return count;
            }

            if (!value) {
              return count + 1;
            }

            return count;
          }, 0);

          const mismatchCount = Number(
            props.isPriceRecalculationEnabled && hasItemPriceMismatch(item, props.allowZeroPrices)
          );

          return {
            invalidItemsCount: acc.invalidItemsCount + invalidFieldsCount,
            invalidCountByPage: {
              ...acc.invalidCountByPage,
              [item.pageNumber]:
                (acc.invalidCountByPage[item.pageNumber] ?? 0) + invalidFieldsCount,
            },
            priceMismatchesCount: acc.priceMismatchesCount + mismatchCount,
            priceMismatchCountByPage: {
              ...acc.priceMismatchCountByPage,
              [item.pageNumber]:
                (acc.priceMismatchCountByPage[item.pageNumber] ?? 0) + mismatchCount,
            },
          };
        },
        {
          invalidItemsCount: 0,
          invalidCountByPage: {} as Record<number, number>,
          priceMismatchesCount: 0,
          priceMismatchCountByPage: {} as Record<number, number>,
        }
      );

    return {
      invalidItemsCount,
      invalidItemsCountPerPage: invalidCountByPage[props.pageNumber] ?? 0,
      invalidCountByPage,
      priceMismatchesCount,
      priceMismatchesCountPerPage: priceMismatchCountByPage[props.pageNumber] ?? 0,
      priceMismatchCountByPage,
    };
  }, [
    props.listOfItems,
    props.isConfirmed,
    props.pageNumber,
    props.allowZeroPrices,
    props.isPriceRecalculationEnabled,
  ]);

  const getPages = useEffectEvent(() => props.documentPreview.state.pages);

  useEffect(
    function updatePageValidation() {
      if (!props.isConfirmed) {
        return;
      }

      getPages().forEach((page) => {
        const invalidCount = result.invalidCountByPage[page.pageNumber];
        const mismatchCount = result.priceMismatchCountByPage[page.pageNumber];
        const status = match({invalidCount, mismatchCount})
          .when(
            ({invalidCount}) => !isNil(invalidCount) && invalidCount > 0,
            () => 'error' as const
          )
          .when(
            ({mismatchCount}) => !isNil(mismatchCount) && mismatchCount > 0,
            () => 'warning' as const
          )
          .when(
            ({invalidCount}) => isNil(invalidCount),
            () => 'default' as const
          )
          .otherwise(() => 'success' as const);

        props.documentPreview.controls.setPageValidation(page.pageNumber, status);
      });
    },
    [
      result.invalidCountByPage,
      result.priceMismatchCountByPage,
      props.isConfirmed,
      props.documentPreview.controls,
    ]
  );

  return result;
}
